import type { SessionMetricPoint } from "api/types/charts";
import type { InsightStat, InsightTone } from "../Targets/TargetInsightCard";
import {
  evaluateMasteryBanner,
  type MasteryBannerCriteria,
  type MasteryBannerPhase,
  type MasteryDirection,
  type MasteryThresholdUnit,
} from "./evaluateMasteryBanner";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const round1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);

/**
 * Primary percentage series (oldest first) for a frequency / discrete-trial
 * target — the metric mastery status is evaluated against.
 * Prefers `percent_correct`, then `percent_independent`; `[]` when neither
 * field carries data.
 */
export function extractPercentSeries(metrics: SessionMetricPoint[]): number[] {
  for (const field of ["percent_correct", "percent_independent"] as const) {
    const values = metrics
      .map((m) => m[field])
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (values.length > 0) return values;
  }
  return [];
}

/** First key in `keys` whose value on `obj` is a finite number, else `null`. */
function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

const THRESHOLD_UNITS: readonly MasteryThresholdUnit[] = [
  "percent",
  "seconds",
  "minutes",
  "rating",
  "occurrences",
];

/**
 * Unit implied by the key the threshold was read from, for legacy criteria that
 * predate `threshold_unit`. `null` means the key carries no unit of its own and
 * the L1 dimension decides.
 */
const UNIT_BY_THRESHOLD_KEY: Record<string, MasteryThresholdUnit | null> = {
  threshold: null,
  value_threshold: null,
  percent_threshold: "percent",
  percent: "percent",
  occurrence_threshold: "occurrences",
};

/**
 * Threshold keys in resolution order. The dimension-appropriate key outranks
 * the others, mirroring `apiCriteriaToFormCriteria` and the backend's
 * `normalizeCriteria` — a criterion carrying both `value_threshold` and
 * `percent_threshold` must resolve to the same number here as it does server
 * side, or the banner would quote a criterion the backend never evaluated.
 */
function thresholdKeysFor(l1Dimension?: string | null): readonly string[] {
  return l1Dimension === "frequency"
    ? ["threshold", "percent_threshold", "occurrence_threshold", "value_threshold", "percent"]
    : ["threshold", "value_threshold", "percent_threshold", "occurrence_threshold", "percent"];
}

/** First key in `keys` holding a finite number, with the key that matched. */
function pickThreshold(
  obj: Record<string, unknown>,
  keys: readonly string[]
): { value: number; key: string } | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "number" && Number.isFinite(value)) return { value, key };
  }
  return null;
}

/**
 * Resolves the unit a criterion's threshold is expressed in, in priority order:
 *
 * 1. An explicit, valid `threshold_unit` — canonical criteria always carry it,
 *    and it is the only signal separating a counter-occurrence frequency target
 *    from a percent one (the L1 dimension is `"frequency"` for both).
 * 2. The key the threshold came from — `occurrence_threshold` is a count,
 *    `percent_threshold`/`percent` a percentage. Derived from the *source* key
 *    rather than mere key presence, so a criterion carrying several threshold
 *    keys is labelled after the one actually used.
 * 3. The L1 dimension, for legacy criteria that carry neither: magnitude is a
 *    rating, duration/latency seconds, everything else percent.
 */
function resolveThresholdUnit(
  obj: Record<string, unknown>,
  sourceKey: string,
  l1Dimension?: string | null
): MasteryThresholdUnit {
  const stored = obj.threshold_unit;
  if (THRESHOLD_UNITS.includes(stored as MasteryThresholdUnit)) {
    return stored as MasteryThresholdUnit;
  }
  const fromKey = UNIT_BY_THRESHOLD_KEY[sourceKey];
  if (fromKey) return fromKey;
  if (l1Dimension === "magnitude") return "rating";
  if (l1Dimension === "duration" || l1Dimension === "latency") return "seconds";
  return "percent";
}

/**
 * Normalises the target's stored criteria JSON to the `MasteryBannerCriteria`
 * shape the evaluator expects. Returns `null` when no threshold is present.
 *
 * The threshold key varies by target: canonical criteria use `threshold` (with
 * a `threshold_unit`), while older/legacy shapes use `percent_threshold`,
 * `value_threshold`, `occurrence_threshold` or the pre-rename `percent`. Reading
 * only the percent keys silently dropped the threshold for every duration,
 * latency and magnitude target — the banner then rendered the literal word
 * "threshold" instead of the number.
 *
 * @param raw - The stored `mastery_criteria` / `regression_criteria` JSON.
 * @param l1Dimension - Target's L1 dimension. Orders the threshold keys (see
 *   {@link thresholdKeysFor}) and is the last-resort unit hint.
 */
export function toBannerCriteria(
  raw: unknown,
  l1Dimension?: string | null
): MasteryBannerCriteria | null {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const threshold = pickThreshold(obj, thresholdKeysFor(l1Dimension));
  if (threshold == null) return null;
  const consecutive = pickNumber(obj, "consecutive_data_points", "consecutive_points");
  return {
    threshold: threshold.value,
    threshold_unit: resolveThresholdUnit(obj, threshold.key, l1Dimension),
    ...(consecutive != null ? { consecutive_points: consecutive } : {}),
  };
}

// ---------------------------------------------------------------------------
// Mastery Status
// ---------------------------------------------------------------------------

export type MasteryStatusLevel = "mastered" | "approaching" | "in-progress";

export interface StatusInsightContent {
  tone: InsightTone;
  title: string;
  subtitle: string;
  stats: InsightStat[];
}

export interface MasteryStatusContent extends StatusInsightContent {
  status: MasteryStatusLevel;
}

/**
 * Derives the Mastery Status card from a target's percentage series and mastery
 * criteria. Reuses {@link evaluateMasteryBanner} (mastery side only) for the
 * "N consecutive at criterion" (mastered) and "2 of 3" (approaching) logic, so
 * the card and the banner never disagree. Direction-aware via `direction`.
 *
 * Only percent criteria are supported: the series comes from
 * {@link extractPercentSeries}, so comparing it against a seconds/rating/count
 * threshold would be a units mismatch that silently mis-reports mastery. A
 * criterion carrying a non-percent `threshold_unit` yields `null` rather than a
 * bogus verdict; an absent unit is treated as percent for backward
 * compatibility. Supporting other dimensions means extracting the matching
 * series here first, not relaxing this guard.
 *
 * The guard lives in this function rather than at the call sites so the card
 * (`TargetChartArea`) and the has-data predicate (`hasDataForChartOption`) can
 * never disagree about whether the card renders.
 *
 * Returns `null` when there's no percentage data, no mastery threshold, or the
 * threshold is not a percentage.
 */
export function deriveMasteryStatus(params: {
  metrics: SessionMetricPoint[];
  masteryCriteria: MasteryBannerCriteria | null | undefined;
  direction: MasteryDirection;
  phase?: MasteryBannerPhase;
}): MasteryStatusContent | null {
  const { metrics, masteryCriteria, direction, phase } = params;
  const unit = masteryCriteria?.threshold_unit;
  if (unit != null && unit !== "percent") return null;
  const series = extractPercentSeries(metrics);
  if (series.length === 0 || masteryCriteria?.threshold == null) return null;

  const evaluation = evaluateMasteryBanner({
    sessions: series.map((percent) => ({ percent })),
    masteryCriteria,
    masteryDirection: direction,
    phase,
  });

  const latest = series[series.length - 1];
  const threshold = masteryCriteria.threshold;
  const stats: InsightStat[] = [
    { label: "Current", value: round1(latest) },
    { label: "Criterion", value: round1(threshold) },
  ];

  if (evaluation?.level === "allTargetsMet") {
    return {
      status: "mastered",
      tone: "success",
      title: "Mastered",
      subtitle: evaluation.detail ?? "",
      stats,
    };
  }
  if (evaluation?.level === "approachingMastery") {
    return {
      status: "approaching",
      tone: "accent",
      title: "Approaching Mastery",
      subtitle: evaluation.detail ?? "",
      stats,
    };
  }
  return {
    status: "in-progress",
    tone: "neutral",
    title: "In Progress",
    subtitle: `Latest ${round1(latest)} vs ${round1(threshold)} criterion`,
    stats,
  };
}

// ---------------------------------------------------------------------------
// Chain Completion Rate (task-analysis)
// ---------------------------------------------------------------------------

/**
 * Derives the Chain Completion Rate card for a task-analysis target: the latest
 * session's `chain_completion_rate` (% of chains completed with every step
 * correct) prominently, with the average and best across sessions as stats.
 * Higher is always better (task analysis is skill-acquisition), so no direction.
 *
 * Returns `null` when no session carries a chain completion rate.
 */
export function deriveChainCompletion(params: {
  metrics: SessionMetricPoint[];
}): StatusInsightContent | null {
  const values = params.metrics
    .map((m) => m.chain_completion_rate)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (values.length === 0) return null;

  const latest = values[values.length - 1];
  const average = values.reduce((sum, v) => sum + v, 0) / values.length;
  const best = Math.max(...values);

  return {
    tone: "accent",
    title: `${round1(latest)}%`,
    subtitle: "of chains completed in the latest session",
    stats: [
      { label: "Average", value: `${round1(average)}%` },
      { label: "Best", value: `${round1(best)}%` },
    ],
  };
}
